import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Calendar, Truck, User, MapPin, Wrench,
  CreditCard, Receipt, Trash2, Loader2, ShieldCheck, FileImage,
  Edit3, FileDown, Plus, X,
} from "lucide-react";
import { toast } from "sonner";

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");
const pmPill = (m) =>
  m === "CASH"     ? "bg-emerald-100 text-emerald-700 border-emerald-200" :
  m === "TRANSFER" ? "bg-blue-100 text-blue-700 border-blue-200" :
  m === "QRIS"     ? "bg-purple-100 text-purple-700 border-purple-200" :
                      "bg-zinc-100 text-zinc-600 border-zinc-200";

export default function BillingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isSuper = (user?.role || "super") === "super";
  const isOfficeOrSuper = ["super", "office"].includes(user?.role || "super");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // v76 — draft
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.billingDetail(id)
      .then(setData)
      .catch(e => {
        toast.error("Gagal muat detail: " + (e?.response?.data?.detail || e.message));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [id]);

  async function onDelete() {
    setDeleting(true);
    try {
      await api.billingDelete(id);
      toast.success("Transaksi billing berhasil dihapus.");
      nav("/rekap-penagihan");
    } catch (e) {
      toast.error("Gagal hapus: " + (e?.response?.data?.detail || e.message));
    } finally { setDeleting(false); }
  }

  function openEdit() {
    setEditing({
      items: (data.items || []).map(it => ({...it})),
      payment_method: data.payment_method,
      notes: data.notes || "",
    });
    setEditOpen(true);
  }

  function updateEditItem(i, field, val) {
    setEditing(e => {
      const items = [...e.items];
      items[i] = { ...items[i], [field]: field === "name" ? val : Number(val || 0) };
      items[i].subtotal = (items[i].qty || 1) * (items[i].price || 0);
      return { ...e, items };
    });
  }

  function addEditRow() {
    setEditing(e => ({ ...e, items: [...e.items, { name: "", qty: 1, price: 0, subtotal: 0 }] }));
  }

  function removeEditRow(i) {
    setEditing(e => ({ ...e, items: e.items.filter((_, j) => j !== i) }));
  }

  const editTotal = (editing?.items || []).reduce((s, it) => s + (it.subtotal || 0), 0);

  async function saveEdit() {
    setSaving(true);
    try {
      const r = await api.billingEdit(id, {
        items: editing.items,
        grand_total: editTotal,
        payment_method: editing.payment_method,
        notes: editing.notes,
      });
      toast.success("Billing berhasil diupdate.");
      setEditOpen(false);
      // Reload
      const fresh = await api.billingDetail(id);
      setData(fresh);
    } catch (e) {
      toast.error("Gagal update: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  }

  function exportPdf() {
    // Simple print → user pilih "Save as PDF" di dialog print
    window.print();
  }

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-zinc-500 gap-2" data-testid="billing-detail-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Memuat detail billing…
    </div>
  );
  if (!data) return (
    <div className="text-center py-16 text-zinc-500" data-testid="billing-detail-empty">
      Data tidak ditemukan. <Link to="/rekap-penagihan" className="text-blue-600 underline">Kembali ke rekap</Link>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="billing-detail-page">
      {/* v84.173 — Print-only invoice header: DESIGN C (Bold Brand-Forward).
          Force-color agar bg hitam & orange TOTAL block tetap muncul di PDF walaupun
          admin lupa centang "Background graphics" di dialog Print. */}
      <div className="hidden print:block mb-4" data-testid="print-header">
        {/* Black hero header */}
        <div className="print-blk px-5 pt-4 pb-3 rounded-t-md">
          <div className="flex items-start justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="print-orange w-11 h-11 rounded-xl flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <div>
                <div className="font-['Manrope'] font-black text-xl text-white leading-none tracking-tight">GLOBAL AC</div>
                <div className="text-[9px] uppercase tracking-[0.3em] font-bold mt-1 print-amber">Banjarbaru · Service Center</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-['Manrope'] font-black text-2xl text-white leading-none">INVOICE</div>
              <div className="font-mono text-[10px] mt-1 font-semibold print-amber">#{data.id.slice(0, 12).toUpperCase()}</div>
              <div className="text-[10px] text-white/80 mt-0.5">{data.date || "—"}</div>
            </div>
          </div>
          {/* Head office horizontal strip (wajib legal) */}
          <div className="mt-2.5 pt-2 border-t border-white/20 text-[8.5px] leading-snug text-white/85 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-bold uppercase tracking-wider text-white">Head Office</span>
            <span className="text-white/40">·</span>
            <span>Jl. Trikora, Komp. Abaditama No.112 RT.39, Banjarbaru, Kalimantan Selatan 70721</span>
            <span className="text-white/40">·</span>
            <span className="font-mono">☎ 0811582226 / 081349778691</span>
            <span className="text-white/40">·</span>
            <span className="font-mono">layanan@globaltehnik.com</span>
            <span className="text-white/40">·</span>
            <span className="font-mono">NPWP CV. Global AC: 71.193.439.8-732.000</span>
          </div>
        </div>
        {/* Orange info strip */}
        <div className="print-orange-strip px-5 py-1.5 text-white text-[10px] font-semibold flex items-center gap-4 flex-wrap rounded-b-md">
          {data.technician?.name && <span>🚐 {data.technician.name.toUpperCase()}</span>}
          <span className="opacity-70">|</span>
          <span>✓ Teknisi Terverifikasi</span>
          <span className="opacity-70">|</span>
          <span className="font-mono">Bank Mandiri a/n Global AC — 0310010390097</span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => nav("/rekap-penagihan")}
            className="h-9 rounded-sm gap-1.5" data-testid="back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Kembali
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#E65100]" /> Detail Billing
            </h1>
            <p className="text-[11px] text-zinc-500 font-mono mt-0.5" data-testid="wo-id">{data.id}</p>
          </div>
        </div>
        {isOfficeOrSuper && (
          <div className="flex items-center gap-2 print:hidden">
            <Button variant="outline" size="sm" onClick={exportPdf}
              className="h-9 rounded-sm gap-1.5" data-testid="export-pdf-btn">
              <FileDown className="w-3.5 h-3.5" /> Export PDF
            </Button>
            {isOfficeOrSuper && (
              <Button variant="outline" size="sm" onClick={openEdit}
                className="h-9 rounded-sm gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="edit-billing-btn">
                <Edit3 className="w-3.5 h-3.5" /> Edit
              </Button>
            )}
            {isOfficeOrSuper && (
            <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="h-9 rounded-sm gap-1.5"
                data-testid="delete-billing-btn">
                <Trash2 className="w-3.5 h-3.5" /> Hapus Transaksi
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-billing-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4 text-red-600" />
                  Apakah Anda yakin ingin menghapus transaksi ini?
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-zinc-600">
                    <p>Aksi ini akan menghapus <strong>billing</strong> dari WO dan mengembalikan status ke <strong>SCHEDULED</strong>.</p>
                    <ul className="list-disc ml-5 text-[12px]">
                      <li>Customer: <b>{data.customer_name || "—"}</b></li>
                      <li>Tanggal: <b>{data.date || "—"}</b></li>
                      <li>Total: <b>{rp(data.grand_total)}</b></li>
                      <li>Metode: <b>{data.payment_method}</b></li>
                    </ul>
                    <p className="text-red-600 font-medium">Aksi tidak bisa di-undo. Hanya super admin yang boleh.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel-btn">Batal</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={onDelete}
                  disabled={deleting}
                  data-testid="delete-confirm-btn">
                  {deleting ? "Menghapus…" : "Ya, Hapus"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}
          </div>
        )}
      </div>

      {/* Meta grid — compact on print (v80) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 print:grid-cols-4 print:gap-2">
        <MetaCard icon={Calendar} label="Tanggal" value={data.date || "—"} testid="meta-date" />
        <MetaCard icon={Truck} label="Armada"
          value={data.technician?.name || "—"} testid="meta-armada" />
        <MetaCard icon={User} label="Customer"
          value={data.customer_name || "—"}
          sub={data.customer_phone} testid="meta-customer" />
        <MetaCard icon={CreditCard} label="Metode Bayar"
          value={data.payment_method}
          pill={pmPill(data.payment_method)} testid="meta-payment" />
      </div>

      {/* Alamat */}
      <div className="border border-zinc-200 rounded-sm bg-white p-4 print:p-2" data-testid="meta-address">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Alamat</div>
            <div className="text-[13px] mt-0.5 print:text-[11px]">{data.address || "—"}</div>
          </div>
        </div>
      </div>

      {/* Rincian items */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden print:border-zinc-400">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-[11px] uppercase tracking-widest text-zinc-600 font-semibold print:hidden">
          Rincian Item ({data.items.length})
        </div>
        {/* v84.173 — Print-only section title (matches Design C zinc-800 bold) */}
        <div className="hidden print:block px-4 py-2 border-b border-zinc-300 text-[11px] uppercase tracking-wider text-zinc-900 font-bold">
          Rincian Pekerjaan
        </div>
        <Table>
          <TableHeader>
            <TableRow className="print:bg-[#0A0E1A]">
              <TableHead className="print:text-white print:font-bold print:uppercase print:text-[10px] print:tracking-wider">Item</TableHead>
              <TableHead className="w-20 text-center print:text-white print:font-bold print:uppercase print:text-[10px] print:tracking-wider">Qty</TableHead>
              <TableHead className="w-32 text-right print:text-white print:font-bold print:uppercase print:text-[10px] print:tracking-wider">Harga</TableHead>
              <TableHead className="w-32 text-right print:text-white print:font-bold print:uppercase print:text-[10px] print:tracking-wider">Subtotal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-zinc-400 py-6">
                  Belum ada item
                </TableCell>
              </TableRow>
            )}
            {data.items.map((it, i) => (
              <TableRow key={i} data-testid={`item-row-${i}`} className={i % 2 === 1 ? "inv-zebra" : ""}>
                <TableCell className="text-[12px] print:font-bold print:text-zinc-900">{it.name || "—"}</TableCell>
                <TableCell className="text-center font-mono text-[12px] print:font-bold print:text-zinc-900">{it.qty || 1}</TableCell>
                <TableCell className="text-right font-mono text-[12px] print:text-zinc-800">{rp(it.price)}</TableCell>
                <TableCell className="text-right font-mono text-[12px] font-semibold print:font-bold print:text-zinc-900">
                  {rp(it.subtotal ?? (it.price * (it.qty || 1)))}
                </TableCell>
              </TableRow>
            ))}
            {/* Total row — hidden on print (diganti dgn BIG ORANGE BLOCK di bawah) */}
            <TableRow className="bg-orange-50 font-bold print:hidden">
              <TableCell colSpan={3} className="text-right text-[11px] uppercase tracking-widest text-[#E65100]">
                Total
              </TableCell>
              <TableCell className="text-right font-mono text-[14px] text-[#E65100]" data-testid="detail-total">
                {rp(data.grand_total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      {/* v84.173 — Print-only BIG ORANGE TOTAL BLOCK (Design C signature) */}
      <div className="hidden print:flex print-orange-block px-5 py-3 text-white items-center justify-between gap-3 mt-2 rounded-md">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold">Total Tagihan</div>
          <div className="text-[9px] text-white/85 mt-0.5">Sudah termasuk semua biaya · Metode: {data.payment_method}</div>
        </div>
        <div className="font-['Manrope'] font-black text-2xl leading-none tracking-tight" data-testid="print-total">
          {rp(data.grand_total)}
        </div>
      </div>

      {/* Jasa teknisi + warranty + notes (internal — hidden on print) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:hidden">
        <div className="border-2 border-blue-600 bg-blue-50 rounded-sm p-4" data-testid="meta-commission">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-blue-700 font-semibold">
            <Wrench className="w-3.5 h-3.5" /> Jasa Teknisi
          </div>
          <div className="text-2xl font-bold text-blue-700 mt-1 font-mono">
            {rp(data.tech_commission)}
          </div>
          <div className="text-[10.5px] text-zinc-500 mt-0.5">
            Auto-hitung dari Item ID di nama item (mis. LC162)
          </div>
        </div>
        <div className="border border-zinc-200 rounded-sm bg-white p-4" data-testid="meta-warranty-notes">
          {data.warranty_status && (
            <div className="flex items-center gap-2 text-[12px] mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-zinc-500">Garansi:</span>
              <Badge className="bg-emerald-100 text-emerald-800 text-[10px] uppercase">
                {data.warranty_status}
              </Badge>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Catatan</div>
          <div className="text-[12px] text-zinc-700 mt-1 whitespace-pre-wrap">
            {data.notes || <span className="text-zinc-400">—</span>}
          </div>
        </div>
      </div>

      {/* Photos (kalau ada) */}
      {data.photos?.length > 0 && (
        <div className="border border-zinc-200 rounded-sm bg-white p-4 print:hidden" data-testid="meta-photos">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
            <FileImage className="w-3.5 h-3.5" /> Foto ({data.photos.length})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.photos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noreferrer"
                className="block aspect-square bg-zinc-100 rounded-sm overflow-hidden hover:opacity-80"
                data-testid={`photo-${i}`}>
                <img src={p} alt={`Foto ${i+1}`} className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* v79 — Print-only footer (thank-you note) */}
      <div className="hidden print:block mt-8 pt-4 border-t border-zinc-300 text-center">
        <div className="text-[11px] text-zinc-600 font-semibold">Terima kasih atas kepercayaan Anda kepada Global AC.</div>
        <div className="text-[9px] text-zinc-400 mt-1">Invoice ini di-generate otomatis oleh sistem dispatch Global AC — tanpa perlu tanda tangan.</div>
      </div>

      {/* v76 — Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="edit-billing-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-blue-600" /> Edit Billing
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Rincian Item</div>
                <div className="border border-zinc-200 rounded-sm max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nama Item</TableHead>
                        <TableHead className="w-16">Qty</TableHead>
                        <TableHead className="w-28">Harga</TableHead>
                        <TableHead className="w-28 text-right">Subtotal</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editing.items.map((it, i) => (
                        <TableRow key={i} data-testid={`edit-item-${i}`}>
                          <TableCell><Input value={it.name || ""} onChange={e => updateEditItem(i, "name", e.target.value)} className="h-8 text-[12px]" /></TableCell>
                          <TableCell><Input type="number" min={1} value={it.qty || 1} onChange={e => updateEditItem(i, "qty", e.target.value)} className="h-8 text-[12px] font-mono" /></TableCell>
                          <TableCell><Input type="number" min={0} value={it.price || 0} onChange={e => updateEditItem(i, "price", e.target.value)} className="h-8 text-[12px] font-mono" /></TableCell>
                          <TableCell className="text-right font-mono text-[12px]">{rp(it.subtotal || 0)}</TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeEditRow(i)} data-testid={`edit-item-remove-${i}`}><X className="w-3.5 h-3.5 text-red-500" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <Button variant="outline" size="sm" onClick={addEditRow} className="mt-2 gap-1 h-8" data-testid="edit-add-item">
                  <Plus className="w-3 h-3" /> Tambah Item
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Metode Bayar</div>
                  <Select value={editing.payment_method} onValueChange={v => setEditing(e => ({...e, payment_method: v}))}>
                    <SelectTrigger className="h-9" data-testid="edit-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="TRANSFER">Transfer</SelectItem>
                      <SelectItem value="QRIS">QRIS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Grand Total</div>
                  <div className="h-9 flex items-center px-3 border border-zinc-200 rounded-sm bg-orange-50 font-mono font-bold text-[#E65100]" data-testid="edit-grand-total">
                    {rp(editTotal)}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Catatan</div>
                <Textarea value={editing.notes} onChange={e => setEditing(x => ({...x, notes: e.target.value}))}
                  rows={2} className="text-[12px]" data-testid="edit-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-cancel-btn">Batal</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-save-btn">
              {saving ? "Menyimpan…" : "Simpan Perubahan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v76 — Print styles (hide sidebar/header saat export PDF) */}
      {/* v84.173 — Force-color agar Design C (black hero + orange TOTAL) tetap
          muncul di PDF meski admin lupa aktifkan "Background graphics". */}
      <style>{`
        .print-blk, .print-orange, .print-orange-strip, .print-orange-block, .print-amber {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .print-blk { background: #0A0E1A !important; }
        .print-orange { background: linear-gradient(135deg, #FF6B35, #E65100) !important; }
        .print-orange-strip { background: linear-gradient(90deg, #E65100 0%, #F57C00 100%) !important; }
        .print-orange-block { background: linear-gradient(135deg, #E65100 0%, #FF6B35 100%) !important; }
        .print-amber { color: #FDBA74 !important; }
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
          aside, header { display: none !important; }
          main { padding: 0 !important; }
          .print\\:hidden { display: none !important; }
          [data-testid="billing-detail-page"] { padding: 0; }
          [data-testid="billing-detail-page"] > * { margin-top: 8px !important; margin-bottom: 8px !important; }
          [data-testid="billing-detail-page"] .grid { break-inside: avoid; }
          .shadow, .shadow-lg { box-shadow: none !important; }
          .border { border-color: #d4d4d8 !important; }
          /* Design C: table header hitam & zebra rows */
          table thead tr { background: #0A0E1A !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table thead th { color: white !important; border-color: #0A0E1A !important; }
          [data-testid="billing-detail-page"] table tbody tr td { background: #FFFFFF !important; color: #18181B !important; opacity: 1 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          [data-testid="billing-detail-page"] table tbody tr.inv-zebra td { background: #F4F4F5 !important; }
          .bg-orange-50, .bg-blue-50 { background: white !important; }
          /* Compact meta cards + rincian */
          [data-testid^="meta-"] { padding: 6px 8px !important; }
          [data-testid^="meta-"] > div > div { line-height: 1.2 !important; }
          table th, table td { padding: 4px 8px !important; }
        }
      `}</style>
    </div>
  );
}

function MetaCard({ icon: Icon, label, value, sub, pill, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      {pill ? (
        <div className={`inline-block mt-1.5 text-[11px] font-bold px-2 py-0.5 rounded border ${pill}`}>
          {value}
        </div>
      ) : (
        <div className="text-[13px] font-semibold mt-1 truncate" title={value}>{value}</div>
      )}
      {sub && <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono truncate">{sub}</div>}
    </div>
  );
}
